import React, { useState, useEffect, useRef, } from 'react';
import { FormGroup, Label, InputGroup, Input, Button, Modal, ModalBody, ModalFooter, Form, } from 'reactstrap';
import { uniq, sum, groupBy, get, keyBy, omit, mapValues, } from 'lodash';
import { useDebounce, useToggle, useList, } from 'react-use';
import { toast, } from 'react-toastify';
import dedent from 'dedent';
import DatePicker from 'react-datepicker';

import okSoundSrc from '../../sounds/ok.mp3';
import firebase from '../../firebase';
import { batch, getDocumentData, getCollectionData, } from '../../shared/firebase';
import { isMobile, safePrefix, } from '../../util';
import env from '../../env';
import { labelString, } from '../../shared/models/product';
import useFormState from '../hooks/useFormState';
import ProductSelectOptionLabel from '../ProductSelectOptionLabel';
import Field from '../Field';
import { idWithTenant, } from '../../shared/util';
import ListForm from '../ListForm';
import PendingItemForm from '../forms/PendingItemForm';
import useCollectionFetch from '../hooks/useCollectionFetch';
import useDocumentSubscription from '../hooks/useDocumentSubscription';

const { entries, } = Object;
const db = firebase.firestore();
const inventoriesRef = db.collection('inventories');
const tenantsRef = db.collection('tenants');

// 鳴かれたバーコードを、在庫の productBarcodeInfo と突き合わせる値に変換する。
// 商品ラベル/FNSKUはそのまま、JANの場合はJANに紐づく商品の商品ラベル(またはFNSKU)へ読み替える。
const resolveBarcodeInfo = async (barcodeValue) => {
  const prefixedValue = safePrefix(barcodeValue);
  const isProductIdBarcode = barcodeValue.match(new RegExp(`^(${env('TENANT_PREFIX')})?[^-]+-\\d+`));
  if(isProductIdBarcode) return prefixedValue;

  // NOTE: FNSKUで登録された在庫はそのまま突合できるので、先にFNSKUとして在庫を探す
  const fnskuInventories = await getCollectionData(
    inventoriesRef.where('status', '==', 'received').where('productBarcodeInfo', '==', prefixedValue).where('locationId', '==', null).limit(1)
  );
  if(fnskuInventories.length > 0) return prefixedValue;

  const janProducts = await getCollectionData(db.collectionGroup('products').where('barcodeInfo', '==', barcodeValue));
  if(janProducts.length === 0) return prefixedValue;

  // NOTE: 同一JANの商品が複数ある場合、入庫待ちのセット完成在庫を持つ商品を優先する
  const candidates = await Promise.all(janProducts.map(async (product) => {
    const inventories = await getCollectionData(
      inventoriesRef.where('status', '==', 'received').where('productBarcodeInfo', '==', labelString(product)).where('locationId', '==', null).limit(1)
    );
    return { product, hasInventory: inventories.length > 0 };
  }));

  return labelString((candidates.find(_ => _.hasInventory) || candidates[0]).product);
};

export default function ShelvingModal(props) {
  const { isOpen, tenants, locations, } = props;
  const tenantsById = keyBy(tenants, 'id');
  const [productBarcodeInfoValue, setProductBarcodeInfoValue] = useState('');
  const [debouncedProductBarcodeInfoValue, setDebouncedProductBarcodeInfoValue] = useState('');
  const [locationKeyValue, setLocationKeyValue] = useState('');
  // NOTE: セット完成在庫のproductBarcodeInfoは商品ラベル(またはFNSKU)で登録されるため、
  // JANを鳴かれた場合はJANから商品を引き当てて、その商品ラベルに読み替えてから在庫を検索する
  const [resolvedBarcodeInfoValue, setResolvedBarcodeInfoValue] = useState('');
  useEffect(() => {
    if(!debouncedProductBarcodeInfoValue) return setResolvedBarcodeInfoValue('');

    let cleanedUp = false;
    (async () => {
      const resolved = await resolveBarcodeInfo(debouncedProductBarcodeInfoValue);
      if(!cleanedUp) setResolvedBarcodeInfoValue(resolved);
    })();

    return _ => cleanedUp = true;
  }, [debouncedProductBarcodeInfoValue]);
  const matchedInventories = useCollectionFetch(resolvedBarcodeInfoValue && inventoriesRef.where('status', '==', 'received').where('productBarcodeInfo', '==', resolvedBarcodeInfoValue).where('locationId', '==', null), [resolvedBarcodeInfoValue]);
  const matchedProduct = useDocumentSubscription(matchedInventories[0] && tenantsRef.doc(matchedInventories[0].tenantId).collection('products').doc(matchedInventories[0].productId), [matchedInventories]);
  const groupedMatchedInventories = groupBy(matchedInventories, _ => _.receivingRequestId || _.combiningRequestId);
  const matchedLocation = locations.find(_ => _.sortKey === locationKeyValue);
  const okAudioEl = useRef(null);
  const productIdInput = useRef(null);
  const locationInput = useRef(null);
  const playAudio = (audio) => {
    audio.currentTime = 0;
    audio.play();
  };
  useDebounce(() => {
    setDebouncedProductBarcodeInfoValue(productBarcodeInfoValue);
    if(productBarcodeInfoValue) locationInput.current.focus();
  }, 300, [productBarcodeInfoValue]);
  const onClickClose = () => {
    const message = dedent`
      商品の入庫する場合は必ずロケーションを鳴いて、数量を指定して入庫をしてください。
      本当にこの画面を閉じますか？
    `;
    if(matchedProduct != null && !window.confirm(message)) return;

    props.onClickClose();
  };

  return (
    <Modal isOpen={isOpen}>
      <div className="modal-header">
        <div className="w-100 d-flex justify-content-between">
          <h5 className="modal-title">
            入庫
          </h5>
        </div>
      </div>
      <ModalBody>
        <FormGroup>
          <Label>商品バーコードまたはJANコード</Label>
          <Input innerRef={productIdInput} value={productBarcodeInfoValue} onChange={_ => setProductBarcodeInfoValue(_.target.value)} />
        </FormGroup>
        <FormGroup>
          <Label>ロケーション</Label>
          <Input innerRef={locationInput} value={locationKeyValue} onChange={_ => setLocationKeyValue(_.target.value)} />
        </FormGroup>
        {
          matchedProduct != null && (
            <div className="card mb-2">
              <div className="card-header">
                見つかった商品
              </div>
              <div className="card-body">
                <ProductSelectOptionLabel admin product={matchedProduct} />
              </div>
            </div>
          )
        }
        {
          matchedInventories.length > 0 && (
            <div className="card">
              <div className="card-header">
                入庫未完了在庫の候補
              </div>
              <div className="card-body p-1">
                <div className="mb-2 d-flex justify-content-end">
                </div>
                <table className="table table-bordered">
                  <thead className="thead-light">
                    <tr>
                      <th style={{ minWidth: 80 }}>依頼ID</th>
                      <th style={{ minWidth: 60 }}>数量</th>
                      <th style={{ minWidth: 100 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {
                      entries(groupedMatchedInventories).map(([requestId, inventories]) => {
                        const onShelved = () => {
                          playAudio(okAudioEl.current);
                          setProductBarcodeInfoValue('');
                          setDebouncedProductBarcodeInfoValue('');
                          setResolvedBarcodeInfoValue('');
                          setLocationKeyValue('');
                          productIdInput.current.focus();
                        };
                        return (
                          <Row key={requestId} {...{ requestId, inventories, matchedLocation, onShelved, }} />
                        );
                      })
                    }
                  </tbody>
                </table>
              </div>
            </div>
          )
        }
      </ModalBody>
      <ModalFooter>
        <Button className="cancel" color="secondary" onClick={onClickClose}>閉じる</Button>
      </ModalFooter>
      <audio src={okSoundSrc} ref={okAudioEl} />
    </Modal>
  );
};

function Row(props) {
  const { requestId, inventories, matchedLocation, onShelved, } = props;
  const type = requestId.startsWith('C') ? 'combiningRequest' : 'receivingRequest';
  const { collectionName } = ({
    combiningRequest: { collectionName: 'combiningRequests', },
    receivingRequest: { collectionName: 'receivingRequests', },
  })[type];
  const [{ tenantId, productId, isNg = false, }] = inventories;
  const [number, setNumber] = useState(1);
  const [expirationDate, setExpirationDate] = useState(null);
  const [isProcessing, toggleProcessing] = useToggle(false);
  const onClickSelect = async () => {
    toggleProcessing(true);
    const request = await getDocumentData(tenantsRef.doc(tenantId).collection(collectionName).doc(requestId));
    const _batch = db.batch();
    const inventoryType = isNg ? 'ng' : 'good';
    const inventoryIdsFieldName = inventoryType + 'InventoryIds';
    const targetInventories = inventories.slice(0, number);
    _batch.update(tenantsRef.doc(tenantId).collection('products').doc(productId), {
      [inventoryIdsFieldName]: firebase.firestore.FieldValue.arrayUnion(...targetInventories.map(_ => _.id)),
      ...(expirationDate != null && { hasExpiration: true, }),
    });
    if(type === 'receivingRequest') {
      _batch.set(request.ref, {
        [[inventoryIdsFieldName, productId].join('-')]: firebase.firestore.FieldValue.arrayUnion(...targetInventories.map(_ => _.id)),
      }, { merge: true });
    }
    try {
      await batch(db, targetInventories, (batch, inventory) => {
        const shelvedAt = new Date();
        batch.update(inventory.ref, {
          locationId: matchedLocation.id,
          locationKey: matchedLocation.sortKey,
          status: 'shelved',
          shelvedAt,
          // NOTE: firstShelvedAtは初回入庫日時なので、既に設定済みの在庫は上書きしない(保管日数の起算日が狂うため)
          ...(inventory.firstShelvedAt == null && { firstShelvedAt: shelvedAt }),
          expirationDate: expirationDate ?? inventory.expirationDate ?? null,
        });
      }, 496, { firstBatch: _batch }); // NOTE: arrayUnion等は2つのbatch処理になるので、2 * 2の4を引いた、496とする
      toast.success('入庫しました');
      onShelved();
    } catch(e) {
      console.error(e);
      toast.error('失敗しました');
    }
    toggleProcessing(false);
  }

  return (
    <tr>
      <td>
        {[tenantId, requestId].join('-')}
      </td>
      <td className="text-right">
        {inventories.length}
      </td>
      <td className="text-right">
        <div className="d-flex flex-column gap-1">
          <DatePicker 
            dateFormat="yyyy/MM/dd" 
            selected={expirationDate} 
            onChange={_ => setExpirationDate(_)} 
            isClearable 
            className="form-control form-control-sm"
            placeholderText="期限日"
          />
          <div className="d-flex text-nowrap gap-1">
            <Input className="text-right p-1" value={number} type="number" onChange={_ => setNumber(parseInt(_.target.value, 10))} />
            <Button color="primary" size="sm" className="" onClick={onClickSelect} disabled={isProcessing || matchedLocation == null || number > inventories.length}>
              入庫
            </Button>
          </div>
        </div>
      </td>
    </tr>
  );
}
